import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Badge, Spinner, Alert,
  Form, InputGroup, Button, Pagination,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import apiService from '../services/api';

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house' },
  { label: 'الفيديوهات', icon: 'bi-camera-video' },
];

const extractYouTubeId = (url) => {
  if (!url) return null;
  // Already a plain ID (11 chars, no slashes)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
};

const VideoCard = ({ video }) => {
  const ytId = extractYouTubeId(video.url);
  const thumbnail = ytId
    ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`
    : null;
  const ytLink = ytId ? `https://www.youtube.com/watch?v=${ytId}` : video.url;

  const hijriDate = [video.DD, video.MM, video.YYYY]
    .filter(Boolean)
    .join('/');

  const types = video.types?.map((t) => t.Name).filter(Boolean) || [];
  const readers = video.readers?.map((r) => r.Name).filter(Boolean) || [];
  const tags = video.tags?.map((t) => t.Name).filter(Boolean) || [];

  return (
    <Card className="h-100 border-0 shadow-sm video-card">
      {/* Thumbnail */}
      <div className="video-thumb-wrap">
        {thumbnail ? (
          <img src={thumbnail} alt={video.Title} className="video-thumb" />
        ) : (
          <div className="video-thumb-placeholder">
            <i className="bi bi-camera-video text-muted" style={{ fontSize: '2rem' }}></i>
          </div>
        )}
        {video.Duration && (
          <span className="duration-badge">{video.Duration}</span>
        )}
        {ytLink && (
          <a
            href={ytLink}
            target="_blank"
            rel="noopener noreferrer"
            className="play-overlay"
            title="فتح في يوتيوب"
          >
            <i className="bi bi-play-circle-fill"></i>
          </a>
        )}
      </div>

      <Card.Body className="p-3">
        <div className="fw-semibold mb-2 video-title" title={video.Title}>
          {video.Title || '—'}
        </div>

        {/* Meta row */}
        <div className="d-flex flex-wrap gap-1 mb-2">
          {types.map((t) => (
            <Badge key={t} bg="primary" style={{ fontSize: '0.7rem' }}>{t}</Badge>
          ))}
          {tags.map((t) => (
            <Badge key={t} bg="secondary" style={{ fontSize: '0.7rem' }}>{t}</Badge>
          ))}
        </div>

        {readers.length > 0 && (
          <div className="text-muted small mb-1">
            <i className="bi bi-person ms-1"></i>
            {readers.join('، ')}
          </div>
        )}

        {hijriDate && (
          <div className="text-muted small">
            <i className="bi bi-calendar3 ms-1"></i>
            {hijriDate} هـ
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageCount: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getVideos({ page, pageSize: 24, search });
      setVideos(data.data || []);
      setPagination(data.meta?.pagination || { page: 1, pageCount: 1, total: 0 });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'تعذّر تحميل الفيديوهات');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const buildPages = () => {
    const { page: current, pageCount } = pagination;
    const pages = [];
    const delta = 2;
    for (let i = Math.max(1, current - delta); i <= Math.min(pageCount, current + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <Layout title="الفيديوهات" breadcrumbs={breadcrumbs}>
      <Container fluid>

        {/* Toolbar */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <Badge bg="secondary" className="fs-6 px-3 py-2">
            <i className="bi bi-camera-video ms-1"></i>
            {pagination.total.toLocaleString('ar-SA')} فيديو
          </Badge>

          <Form onSubmit={handleSearch} className="d-flex gap-2">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="ابحث بالعنوان..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ minWidth: '220px' }}
              />
              <Button type="submit" variant="primary">
                <i className="bi bi-search"></i>
              </Button>
              {search && (
                <Button
                  variant="outline-secondary"
                  onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                >
                  <i className="bi bi-x"></i>
                </Button>
              )}
            </InputGroup>
          </Form>
        </div>

        {error && (
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle ms-2"></i>{error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : videos.length === 0 ? (
          <Alert variant="info" className="text-center">
            <i className="bi bi-info-circle ms-2"></i>
            لا توجد نتائج{search ? ` للبحث عن "${search}"` : ''}.
          </Alert>
        ) : (
          <>
            <Row className="g-3">
              {videos.map((video) => (
                <Col xs={12} sm={6} md={4} xl={3} key={video.documentId}>
                  <VideoCard video={video} />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {pagination.pageCount > 1 && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination>
                  <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                  <Pagination.Prev onClick={() => setPage((p) => p - 1)} disabled={page === 1} />
                  {buildPages().map((p) => (
                    <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                      {p}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => setPage((p) => p + 1)} disabled={page === pagination.pageCount} />
                  <Pagination.Last onClick={() => setPage(pagination.pageCount)} disabled={page === pagination.pageCount} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Container>

      <style>{`
        .video-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          overflow: hidden;
        }
        .video-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 0.5rem 1.2rem rgba(0,0,0,0.12) !important;
        }
        .video-thumb-wrap {
          position: relative;
          background: #000;
          aspect-ratio: 16/9;
          overflow: hidden;
        }
        .video-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.9;
          transition: opacity 0.2s;
        }
        .video-card:hover .video-thumb { opacity: 1; }
        .video-thumb-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
        }
        .duration-badge {
          position: absolute;
          bottom: 6px;
          left: 6px;
          background: rgba(0,0,0,0.75);
          color: #fff;
          font-size: 0.72rem;
          padding: 1px 5px;
          border-radius: 3px;
        }
        .play-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0);
          font-size: 3rem;
          transition: color 0.2s;
          text-decoration: none;
        }
        .video-card:hover .play-overlay { color: rgba(255,255,255,0.9); }
        .video-title {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          font-size: 0.9rem;
          line-height: 1.4;
          min-height: 2.5rem;
        }
      `}</style>
    </Layout>
  );
};

export default Videos;
